import React, { useState } from 'react'
import { isNil, noop } from 'lodash-es'
import { CommittingServicingEvent, DealDetail, DealStatus, User, UserPreference } from 'api'
import { Locale, Translation } from 'locale'
import {
  Card,
  CardContent,
  CardHeader,
  DetailColumn,
  DetailRow,
  Details,
  DirectionLabel,
  Headline,
  KeyInfo,
  Status,
  StatusStrip,
  Substatus,
  TicketButton
} from './cardStyles'
import { TextButtonType } from 'view/components/buttons'
import { getOwnDirection, getRateFormatOptions, makeFormatDirection } from '../datagrid'
import {
  getDealHeadline,
  getDealIcon,
  getDealKeyInfo,
  getDealStatusTextDone,
  getDealSubstatusText,
  getNegotiatedValue,
  getServicingHeadline,
  getServicingIcon,
  getServicingStatusColor,
  getServicingStatusText,
  isImportantServicingEvent
} from 'view/routes/deals/components/utils'
import { iconSet } from 'view/components/icons'
import { getUserFullName } from 'model'

export interface DealCardProps {
  dealDetail: DealDetail
  userPreference: UserPreference
  isAdminUser: boolean
  servicingEvent?: CommittingServicingEvent
  inTicket?: boolean
  onClick?: () => unknown
  isAdminAmendAndWithOtherUser: boolean
  amendingUser: User | null
}

export const DealCard: React.FunctionComponent<DealCardProps> = props => {
  const [isHover, setIsHover] = useState(false)

  const { dealDetail, userPreference, isAdminUser, servicingEvent, inTicket, onClick, isAdminAmendAndWithOtherUser, amendingUser } = props
  const { deal, servicingSideDetails, servicingSideDetailsMeta, referenceRate } = dealDetail

  const coloredHeader = !servicingEvent || isImportantServicingEvent(servicingEvent)
  const statusColor = servicingEvent ? getServicingStatusColor(servicingEvent) : 'initial'
  // this was changed in bq-4408 but changing it back as we want initiator view for admin key info.
  const ownOrder = dealDetail.ownOrder
  const rate = servicingSideDetails ? getNegotiatedValue(servicingSideDetails?.negotiationFields.RATE?.negotiableFieldRequired) : deal.rate
  const rateString = isNil(rate) ? '' : ` @ ${Locale.formatNumber(rate, getRateFormatOptions(referenceRate))}`
  const direction = getOwnDirection(deal.direction, ownOrder)
  const showTicketButton = !inTicket && isHover
  const otherUserAmending = isAdminAmendAndWithOtherUser && amendingUser ? ` (${getUserFullName(amendingUser)})` : ``

  return (
    <Card
      dead={deal.status === DealStatus.CANCELLED}
      inTicket={inTicket}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}>
      {!inTicket && statusColor && <StatusStrip statusColor={statusColor} />}
      <CardContent>
        <CardHeader statusColor={coloredHeader && statusColor ? statusColor : 'initial'}>
          <Status>
            {!inTicket && iconSet.logoIcon}
            <span>
              {servicingEvent
                ? getServicingStatusText(servicingEvent, dealDetail.deal.servicingDetails?.servicingType.servicingType || undefined)
                : getDealStatusTextDone(dealDetail, isAdminUser)}
              {otherUserAmending}
              {rateString}
            </span>
          </Status>
          <Substatus grey={deal.amended}>
            {!servicingEvent && <span>{getDealSubstatusText(deal)}</span>}
            <span>{servicingEvent ? getServicingIcon(servicingEvent) : getDealIcon(deal.status)}</span>
          </Substatus>
        </CardHeader>
        <Details>
          <DetailColumn>
            <DetailRow>
              <KeyInfo>{getDealKeyInfo({ ...dealDetail, ownOrder })}</KeyInfo>
              {!showTicketButton && (
                <span>{Locale.formatTime((servicingEvent && deal.servicingDetails?.createdAt) ?? deal.createdAt)}</span>
              )}
            </DetailRow>
            <DetailRow>
              <Headline>
                <DirectionLabel direction={direction}>{makeFormatDirection(userPreference.directionFormat)(direction)}</DirectionLabel>
                {servicingSideDetails
                  ? getServicingHeadline(servicingSideDetails, servicingSideDetailsMeta, userPreference)
                  : getDealHeadline(dealDetail, userPreference)}
              </Headline>
            </DetailRow>
          </DetailColumn>
          {showTicketButton && (
            <DetailColumn>
              <TicketButton type={TextButtonType.Primary} onClick={onClick ?? noop}>
                {Locale.translate(Translation.TC_BUTTON_VIEW_TICKET)}
              </TicketButton>
            </DetailColumn>
          )}
        </Details>
      </CardContent>
    </Card>
  )
}
